const NOTIFICATION_CRYPTO_DB_NAME = 'nettgrav-messenger-v2-notification-crypto';
const NOTIFICATION_CRYPTO_DB_VERSION = 1;
const NOTIFICATION_HISTORY_KEY_STORE = 'history_keys';
const HISTORY_STORAGE_PREFIX = 'messenger-v2-history-key:';

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const openNotificationCryptoDb = () =>
  new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }

    const request = indexedDB.open(
      NOTIFICATION_CRYPTO_DB_NAME,
      NOTIFICATION_CRYPTO_DB_VERSION,
    );
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(NOTIFICATION_HISTORY_KEY_STORE)) {
        db.createObjectStore(NOTIFICATION_HISTORY_KEY_STORE, { keyPath: 'conversationId' });
      }
    };
    request.onerror = () => resolve(null);
    request.onsuccess = () => resolve(request.result);
  });

const runNotificationCryptoRequest = async (mode, callback, fallback = null) => {
  const db = await openNotificationCryptoDb();
  if (!db) return fallback;

  return new Promise((resolve) => {
    try {
      const transaction = db.transaction(NOTIFICATION_HISTORY_KEY_STORE, mode);
      const request = callback(transaction.objectStore(NOTIFICATION_HISTORY_KEY_STORE));
      request.onerror = () => resolve(fallback);
      request.onsuccess = () => resolve(request.result);
    } catch {
      resolve(fallback);
    }
  });
};

export const persistMessengerV2NotificationHistoryKey = async ({
  conversationId,
  keyB64,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedKey = toStringOrNull(keyB64);
  if (!normalizedConversationId || !normalizedKey) return null;

  await runNotificationCryptoRequest(
    'readwrite',
    (store) =>
      store.put({
        conversationId: normalizedConversationId,
        keyB64: normalizedKey,
        updatedAt: Date.now(),
      }),
    null,
  );
  return normalizedKey;
};

export const loadMessengerV2NotificationHistoryKey = async (conversationId) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) return null;
  const record = await runNotificationCryptoRequest(
    'readonly',
    (store) => store.get(normalizedConversationId),
    null,
  );
  return toStringOrNull(record?.keyB64 ?? null);
};

export const syncMessengerV2NotificationHistoryKeys = async () => {
  if (typeof window === 'undefined' || !window.localStorage) return 0;

  const records = [];
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const storageKey = window.localStorage.key(index);
      if (!storageKey?.startsWith(HISTORY_STORAGE_PREFIX)) continue;
      const conversationId = toStringOrNull(storageKey.slice(HISTORY_STORAGE_PREFIX.length));
      const keyB64 = toStringOrNull(window.localStorage.getItem(storageKey));
      if (conversationId && keyB64) records.push({ conversationId, keyB64 });
    }
  } catch {
    return 0;
  }

  await Promise.all(records.map(persistMessengerV2NotificationHistoryKey));
  return records.length;
};

export const MESSENGER_V2_NOTIFICATION_CRYPTO_DB = Object.freeze({
  name: NOTIFICATION_CRYPTO_DB_NAME,
  version: NOTIFICATION_CRYPTO_DB_VERSION,
  storeName: NOTIFICATION_HISTORY_KEY_STORE,
});
